import { PaymentProviderEnum } from '../enums';
import { PaymentReturnUrlTypeEnum, PurchasePaymentTypeEnum } from '../models/crm/types';

import { NullableString } from './types';

const getLocationSearch = (): string => {
  if (typeof globalThis === 'undefined') return '';
  const location = (globalThis as { location?: { search?: string } }).location;
  return location?.search ?? '';
};

const getUrlParams = (): URLSearchParams => new URLSearchParams(getLocationSearch());

export const getPaymentReferenceId = (): NullableString => {
  const urlParams = getUrlParams();
  return urlParams.get('paymentReferenceId');
};

export const getPurchasedPlanId = (): number | null => {
  const urlParams = getUrlParams();
  const planId = urlParams.get('planId');
  return planId ? Number(planId) : null;
};

export const getPaymentProvider = (): PaymentProviderEnum | undefined => {
  const urlParams = getUrlParams();
  const paymentProviderValue = urlParams.get('paymentProvider');
  if (!paymentProviderValue) return undefined;

  const paymentProviderValueNum = Number(paymentProviderValue);

  if (Object.values(PaymentProviderEnum).includes(paymentProviderValueNum)) {
    return paymentProviderValueNum as PaymentProviderEnum;
  }

  return undefined;
};

export const isPaymentCallback = (): boolean => {
  const urlParams = getUrlParams();
  return !!getPaymentReferenceId() && !!urlParams.get('callbackReturnType');
};

/**
 * Only a *successful* plan purchase return counts as a plan callback — the cancel and fail return
 * URLs carry the same purchaseType, but nothing was bought, so the account poll and the
 * post-purchase notifications must not be triggered for them.
 */
export const isPlanPurchasePaymentCallback = (): boolean => {
  const urlParams = getUrlParams();
  return isPaymentSuccessCallback() && (urlParams.get('purchaseType') === String(PurchasePaymentTypeEnum.Plan)
    || urlParams.get('purchaseType') === String(PurchasePaymentTypeEnum.PlanWithAutoPass));
};

/**
 * The plan purchase callback is only recognisable from the URL the payment provider redirects to,
 * and that URL is short-lived — the app navigates to the overview right away. Anything that has to
 * react to the callback later (the accounts-summary poll waiting for the purchased account) reads
 * this sessionStorage flag instead, so mark it as early as the callback URL is observed.
 */
export const PLAN_PURCHASE_CALLBACK_KEY = 'pollingPlanCallback';

export const markPlanPurchasePaymentCallback = (): boolean => {
  const isPlanCallback = isPlanPurchasePaymentCallback();
  if (isPlanCallback) {
    sessionStorage.setItem(PLAN_PURCHASE_CALLBACK_KEY, 'true');
  }
  return isPlanCallback;
};

/**
 * True while a plan purchase callback is being processed — either the callback params are still in
 * the URL, or they were seen earlier in this session and the purchased account is still awaited.
 */
export const clearPlanPurchasePaymentCallback = (): void =>
  sessionStorage.removeItem(PLAN_PURCHASE_CALLBACK_KEY);

export const isPlanPurchaseCallbackInProgress = (): boolean =>
  markPlanPurchasePaymentCallback() || !!sessionStorage.getItem(PLAN_PURCHASE_CALLBACK_KEY);

export const isAutoPassPurchase = (): boolean => {
  const urlParams = getUrlParams();
  return urlParams.get('purchaseType') === String(PurchasePaymentTypeEnum.AutoPass);
};

export const isAutoPassPurchasePaymentCallback = (): boolean =>
  isPaymentSuccessCallback() && isAutoPassPurchase();

export const isPlanWithAutoPassPurchasePaymentCallback = (): boolean => {
  const urlParams = getUrlParams();
  return isPaymentCallback() && urlParams.get('purchaseType') === String(PurchasePaymentTypeEnum.PlanWithAutoPass);
};

export const isPaymentSuccessCallback = (): boolean => {
  const urlParams = getUrlParams();
  return !!getPaymentReferenceId() && urlParams.get('callbackReturnType') === String(PaymentReturnUrlTypeEnum.Success);
};
